import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Componente de estimativa de entrega
 * Detecta localização do usuário por IP e exibe prazos de entrega automaticamente
 */
export class DeliveryEstimateComponent extends Component {
  requiredRefs = ['loading', 'error', 'options', 'standardOption', 'deliveryDate', 'flagContainer', 'countryText'];

  #abortController = new AbortController();
  #currentCountry = null;
  #deliveryRanges = {
    'US': { min: 3, max: 6, region: 'United States' },
    'CA': { min: 4, max: 7, region: 'Canada' },
    'GB': { min: 2, max: 6, region: 'United Kingdom' },
    'BR': { min: 5, max: 10, region: 'Brazil' },
    'AU': { min: 4, max: 9, region: 'Australia / New Zealand' },
    'NZ': { min: 4, max: 9, region: 'Australia / New Zealand' },
    'EU': { min: 3, max: 8, region: 'Europe' },
    'DEFAULT': { min: 7, max: 15, region: 'Rest of the world' }
  };

  // Países da Europa
  #europeanCountries = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 
    'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
  ];

  connectedCallback() {
    super.connectedCallback();
    
    requestAnimationFrame(() => {
      if (!this.refs.loading || !this.refs.options) {
        console.error('DeliveryEstimateComponent: Refs não encontrados');
        return;
      }
      
      const { signal } = this.#abortController;
      
      // Escutar atualizações de variante
      const target = this.closest('.shopify-section, dialog, product-card');
      target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
      
      // Detectar localização por IP
      this.#detectLocationByIP();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Atualiza o variant ID quando a variante muda
   */
  #onVariantUpdate = (event) => {
    if (event.detail.resource?.id) {
      this.dataset.variantId = event.detail.resource.id;
    }
  };

  /**
   * Detecta a localização do usuário por IP
   */
  async #detectLocationByIP() {
    console.log('[Delivery Estimate] Iniciando detecção de localização...');
    this.#showLoading();
    this.#hideError();
    this.#hideOptions();

    try {
      // Tentar obter do localStorage primeiro (cache)
      const cachedLocation = localStorage.getItem('delivery_location');
      if (cachedLocation) {
        try {
          const location = JSON.parse(cachedLocation);
          const cacheTime = location.timestamp || 0;
          const now = Date.now();
          
          // Validar se o código do país tem 2 letras (formato correto)
          const isValidCode = location.countryCode && 
                              typeof location.countryCode === 'string' && 
                              location.countryCode.length === 2;
          
          // Cache válido por 24 horas E código válido
          if (now - cacheTime < 24 * 60 * 60 * 1000 && isValidCode) {
            console.log('[Delivery Estimate] Usando localização do cache:', location);
            this.#displayDeliveryEstimate(location.countryCode, location.countryName);
            return;
          } else {
            if (!isValidCode) {
              console.log('[Delivery Estimate] Cache inválido (código do país incorreto), buscando nova localização...');
              // Limpar cache inválido
              localStorage.removeItem('delivery_location');
            } else {
              console.log('[Delivery Estimate] Cache expirado, buscando nova localização...');
            }
          }
        } catch (e) {
          console.warn('[Delivery Estimate] Erro ao ler cache de localização:', e);
          // Limpar cache corrompido
          localStorage.removeItem('delivery_location');
        }
      } else {
        console.log('[Delivery Estimate] Nenhum cache encontrado, buscando localização...');
      }

      // Tentar múltiplas APIs de geolocalização como fallback
      let countryCode = 'US';
      let countryName = 'United States';
      let data = null;

      // API 1: geojs.io (gratuita, sem chave) - busca código do país
      try {
        console.log('[Delivery Estimate] Tentando geojs.io...');
        // Primeiro buscar o código do país (2 letras)
        const countryResponse = await fetch('https://get.geojs.io/v1/ip/country.json', {
          signal: this.#abortController.signal
        });
        if (countryResponse.ok) {
          const countryData = await countryResponse.json();
          if (countryData.country && countryData.country.length === 2) {
            countryCode = countryData.country.toUpperCase();
            // Buscar nome do país
            const geoResponse = await fetch('https://get.geojs.io/v1/ip/geo.json', {
              signal: this.#abortController.signal
            });
            if (geoResponse.ok) {
              const geoData = await geoResponse.json();
              countryName = geoData.country || countryCode;
            } else {
              countryName = countryCode;
            }
            data = { countryCode, country: countryName };
            console.log('[Delivery Estimate] geojs.io funcionou!');
          }
        }
      } catch (error1) {
        console.warn('[Delivery Estimate] geojs.io falhou, tentando próxima API...', error1);
      }

      // API 2: ipapi.co (se a primeira falhar)
      if (!data || !countryCode || countryCode.length !== 2) {
        try {
          console.log('[Delivery Estimate] Tentando ipapi.co...');
          const response2 = await fetch('https://ipapi.co/json/', {
            signal: this.#abortController.signal
          });
          if (response2.ok) {
            const ipapiData = await response2.json();
            if (ipapiData.country_code) {
              countryCode = ipapiData.country_code;
              countryName = ipapiData.country_name || countryCode;
              data = { countryCode, country: countryName };
              console.log('[Delivery Estimate] ipapi.co funcionou!');
            }
          }
        } catch (error2) {
          console.warn('[Delivery Estimate] ipapi.co falhou, tentando próxima API...', error2);
        }
      }

      // API 3: ip-api.com (como último recurso, mesmo que tenha dado 403 antes)
      if (!data || !countryCode || countryCode.length !== 2) {
        try {
          console.log('[Delivery Estimate] Tentando ip-api.com...');
          const response3 = await fetch('https://ip-api.com/json/?fields=status,countryCode,country', {
            signal: this.#abortController.signal
          });
          if (response3.ok) {
            const ipapiData = await response3.json();
            if (ipapiData.status === 'success' && ipapiData.countryCode) {
              countryCode = ipapiData.countryCode;
              countryName = ipapiData.country || countryCode;
              data = { countryCode, country: countryName };
              console.log('[Delivery Estimate] ip-api.com funcionou!');
            }
          }
        } catch (error3) {
          console.warn('[Delivery Estimate] ip-api.com falhou', error3);
        }
      }

      // Se todas as APIs falharam, usar fallback
      if (!data || !countryCode || countryCode.length !== 2) {
        throw new Error('Todas as APIs de geolocalização falharam');
      }

      console.log('[Delivery Estimate] Resposta da API:', data);

      console.log(`[Delivery Estimate] Localização detectada: ${countryName} (${countryCode})`);

      // Salvar no cache
      localStorage.setItem('delivery_location', JSON.stringify({
        countryCode,
        countryName,
        timestamp: Date.now()
      }));

      this.#displayDeliveryEstimate(countryCode, countryName);

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('[Delivery Estimate] Detecção cancelada (componente desconectado)');
        return; // Componente foi desconectado
      }
      
      console.error('[Delivery Estimate] Erro ao detectar localização:', error);
      console.log('[Delivery Estimate] Usando fallback: Estados Unidos (US)');
      
      // Fallback: usar Estados Unidos como padrão
      this.#displayDeliveryEstimate('US', 'United States');
    } finally {
      this.#hideLoading();
    }
  }

  /**
   * Exibe a estimativa de entrega baseada no país
   */
  #displayDeliveryEstimate(countryCode, countryName) {
    this.#currentCountry = countryCode;
    
    // Determinar a região
    let region = this.#getRegionForCountry(countryCode);
    let deliveryRange = this.#deliveryRanges[region] || this.#deliveryRanges['DEFAULT'];
    
    console.log(`[Delivery Estimate] Região: ${deliveryRange.region}, Prazo: ${deliveryRange.min}-${deliveryRange.max} dias úteis`);
    
    // Atualizar bandeira
    this.#updateFlag(countryCode);

    // Exibir texto "enviamos para {país}" (template configurável no editor de tema)
    this.#updateCountryText(countryName);

    // Calcular e exibir data de entrega
    const today = new Date();
    const minDate = this.#calculateDeliveryDate(today, deliveryRange.min);
    const maxDate = this.#calculateDeliveryDate(today, deliveryRange.max);
    
    const dateText = this.#formatDeliveryDateRange(minDate, maxDate);
    this.refs.deliveryDate.innerHTML = dateText;
    
    console.log(`[Delivery Estimate] Prazo exibido: ${dateText}`);
    
    this.#showOptions();
  }

  /**
   * Determina a região baseada no código do país
   */
  #getRegionForCountry(countryCode) {
    if (countryCode === 'US') return 'US';
    if (countryCode === 'CA') return 'CA';
    if (countryCode === 'GB') return 'GB';
    if (countryCode === 'BR') return 'BR';
    if (countryCode === 'AU' || countryCode === 'NZ') return countryCode;
    if (this.#europeanCountries.includes(countryCode)) return 'EU';
    return 'DEFAULT';
  }

  /**
   * Calcula a data de entrega considerando apenas dias úteis
   */
  #calculateDeliveryDate(startDate, businessDays) {
    let currentDate = new Date(startDate);
    let daysAdded = 0;

    while (daysAdded < businessDays) {
      currentDate.setDate(currentDate.getDate() + 1);
      const dayOfWeek = currentDate.getDay();
      // Ignorar sábado (6) e domingo (0)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        daysAdded++;
      }
    }

    return currentDate;
  }

  /**
   * Formata o intervalo de datas de entrega
   */
  #formatDeliveryDateRange(minDate, maxDate) {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const minDay = minDate.getDate();
    const minMonth = months[minDate.getMonth()];
    const maxDay = maxDate.getDate();
    const maxMonth = months[maxDate.getMonth()];
    
    if (minMonth === maxMonth && minDay === maxDay) {
      return `por <strong>${minDay} ${minMonth}</strong>`;
    } else if (minMonth === maxMonth) {
      return `Your delivery will take place between <strong>${minMonth} ${minDay} and ${maxDay}</strong>`;
    } else {
      return `Your delivery will take place between <strong>${minMonth} ${minDay} and ${maxMonth} ${maxDay}</strong>`;
    }
  }

  /**
   * Mostra o ícone de caminhão (quando não há localização detectada)
   */
  #showTruckIcon() {
    if (!this.refs.flagContainer) return;
    
    this.refs.flagContainer.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 640 512" fill="none" xmlns="http://www.w3.org/2000/svg" class="delivery-estimate__truck-icon">
        <path d="M48 0C21.5 0 0 21.5 0 48V368c0 26.5 21.5 48 48 48H64c0 53 43 96 96 96s96-43 96-96H384c0 53 43 96 96 96s96-43 96-96h32c17.7 0 32-14.3 32-32s-14.3-32-32-32V288 256 237.3c0-17-6.7-33.3-18.7-45.3L512 114.7c-12-12-28.3-18.7-45.3-18.7H416V48c0-26.5-21.5-48-48-48H48zM416 160h50.7L544 237.3V256H416V160zM112 416a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm368-48a48 48 0 1 1 0 96 48 48 0 1 1 0-96z" fill="#000000"/>
      </svg>
    `;
  }

  /**
   * Exibe o texto "We ship to {country}" usando o template configurado no editor de tema
   * (data-country-template, com o token {country} substituído pelo nome do país detectado).
   */
  #updateCountryText(countryName) {
    if (!this.refs.countryText) return;
    const template = this.getAttribute('data-country-template') || 'We ship to {country}';
    this.refs.countryText.textContent = template.replace('{country}', countryName);
    this.refs.countryText.classList.remove('hidden');
  }

  /**
   * Atualiza a bandeira baseado no país
   */
  #updateFlag(countryCode) {
    if (!this.refs.flagContainer) return;
    
    const countryLower = countryCode.toLowerCase();
    
    // Tentar diferentes formas de acessar o atributo
    let flagUrl = this.getAttribute(`data-flag-${countryLower}`) || 
                  this.dataset[`flag${countryLower.charAt(0).toUpperCase() + countryLower.slice(1)}`] ||
                  this.dataset[`flag-${countryLower}`];
    
    // Sempre tentar carregar o arquivo (PNG ou SVG)
    if (flagUrl && flagUrl.trim() !== '') {
      console.log(`Tentando carregar bandeira ${countryCode} de: ${flagUrl}`);
      this.#loadFlagFromFile(flagUrl, countryCode);
    } else {
      console.warn(`URL da bandeira ${countryCode} não encontrada, usando SVG inline`);
      // Fallback para SVGs inline apenas se não houver URL configurada
      this.refs.flagContainer.innerHTML = this.#getFlagSVG(countryCode);
    }
  }

  /**
   * Carrega a bandeira de um arquivo (PNG ou SVG)
   */
  async #loadFlagFromFile(url, countryCode) {
    try {
      const urlWithoutParams = url.split('?')[0].toLowerCase();
      
      const isImage = urlWithoutParams.endsWith('.png') || 
                      urlWithoutParams.endsWith('.jpg') || 
                      urlWithoutParams.endsWith('.jpeg') ||
                      urlWithoutParams.endsWith('.webp');
      
      if (isImage) {
        console.log(`Carregando imagem PNG: ${url}`);
        const img = document.createElement('img');
        img.src = url;
        img.alt = `Bandeira ${countryCode}`;
        img.style.width = '24px';
        img.style.height = '18px';
        img.style.objectFit = 'contain';
        img.style.display = 'block';
        
        this.refs.flagContainer.innerHTML = '';
        this.refs.flagContainer.appendChild(img);
        
        const timeout = setTimeout(() => {
          if (!img.complete || img.naturalHeight === 0) {
            console.warn(`Timeout ao carregar PNG ${countryCode}, usando SVG inline`);
            this.refs.flagContainer.innerHTML = this.#getFlagSVG(countryCode);
          }
        }, 3000);
        
        img.onload = () => {
          clearTimeout(timeout);
          console.log(`PNG ${countryCode} carregado com sucesso!`);
        };
        
        img.onerror = () => {
          clearTimeout(timeout);
          console.warn(`Erro ao carregar PNG ${countryCode} de ${url}, usando SVG inline`);
          this.refs.flagContainer.innerHTML = this.#getFlagSVG(countryCode);
        };
      } else {
        console.log(`Carregando SVG: ${url}`);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);
        
        try {
          const response = await fetch(url, { 
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok) {
            const svgText = await response.text();
            if (svgText.trim().startsWith('<svg') || svgText.trim().startsWith('<?xml')) {
              this.refs.flagContainer.innerHTML = svgText;
              console.log(`SVG ${countryCode} carregado com sucesso!`);
            } else {
              throw new Error('Arquivo não é um SVG válido');
            }
          } else {
            throw new Error('Arquivo não encontrado');
          }
        } catch (fetchError) {
          clearTimeout(timeoutId);
          if (fetchError.name === 'AbortError') {
            throw new Error('Timeout ao carregar arquivo');
          }
          throw fetchError;
        }
      }
    } catch (error) {
      console.warn(`Erro ao carregar bandeira ${countryCode}:`, error.message);
      this.refs.flagContainer.innerHTML = this.#getFlagSVG(countryCode);
    }
  }

  /**
   * Retorna SVG inline da bandeira como fallback
   */
  #getFlagSVG(countryCode) {
    const flags = {
      'BR': `<svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="18" fill="#009739"/>
        <path d="M0 6H24V12H0V6Z" fill="#FEDD00"/>
        <path d="M12 0C8.134 0 5 3.134 5 7C5 10.866 8.134 14 12 14C15.866 14 19 10.866 19 7C19 3.134 15.866 0 12 0Z" fill="#002776"/>
        <path d="M12 4.5C10.343 4.5 9 5.843 9 7.5C9 9.157 10.343 10.5 12 10.5C13.657 10.5 15 9.157 15 7.5C15 5.843 13.657 4.5 12 4.5Z" fill="#FEDD00"/>
      </svg>`,
      'US': `<svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="18" fill="#B22234"/>
        <rect y="2" width="24" height="2" fill="#FFFFFF"/>
        <rect y="4" width="24" height="2" fill="#3C3B6E"/>
        <rect y="6" width="24" height="2" fill="#FFFFFF"/>
        <rect y="8" width="24" height="2" fill="#3C3B6E"/>
        <rect y="10" width="24" height="2" fill="#FFFFFF"/>
        <rect y="12" width="24" height="2" fill="#3C3B6E"/>
        <rect y="14" width="24" height="2" fill="#FFFFFF"/>
        <rect y="16" width="24" height="2" fill="#3C3B6E"/>
        <rect width="10" height="10" fill="#3C3B6E"/>
      </svg>`,
      'CA': `<svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="18" fill="#FF0000"/>
        <rect x="8" width="8" height="18" fill="#FFFFFF"/>
        <path d="M12 4L11 7H13L12 4Z" fill="#FF0000"/>
        <path d="M12 14L11 11H13L12 14Z" fill="#FF0000"/>
        <path d="M8 9L5 8L8 7L8 9Z" fill="#FF0000"/>
        <path d="M16 9L19 8L16 7L16 9Z" fill="#FF0000"/>
      </svg>`,
      'GB': `<svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="18" fill="#012169"/>
        <path d="M0 0L24 18M24 0L0 18" stroke="#FFFFFF" stroke-width="2"/>
        <path d="M0 0L24 18M24 0L0 18" stroke="#C8102E" stroke-width="1.2"/>
        <path d="M12 0V18M0 9H24" stroke="#FFFFFF" stroke-width="3"/>
        <path d="M12 0V18M0 9H24" stroke="#C8102E" stroke-width="2"/>
      </svg>`,
      'FR': `<svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="8" height="18" fill="#002654"/>
        <rect x="8" width="8" height="18" fill="#FFFFFF"/>
        <rect x="16" width="8" height="18" fill="#ED2939"/>
      </svg>`,
      'DE': `<svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="6" fill="#000000"/>
        <rect y="6" width="24" height="6" fill="#DD0000"/>
        <rect y="12" width="24" height="6" fill="#FFCE00"/>
      </svg>`,
      'ES': `<svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="6" fill="#AA151B"/>
        <rect y="6" width="24" height="6" fill="#F1BF00"/>
        <rect y="12" width="24" height="6" fill="#AA151B"/>
      </svg>`,
      'IT': `<svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="8" height="18" fill="#009246"/>
        <rect x="8" width="8" height="18" fill="#FFFFFF"/>
        <rect x="16" width="8" height="18" fill="#CE2B37"/>
      </svg>`,
      'PT': `<svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="12" height="18" fill="#006600"/>
        <rect x="12" width="12" height="18" fill="#FF0000"/>
        <circle cx="6" cy="9" r="3" fill="#FFD700"/>
        <circle cx="6" cy="9" r="2" fill="#006600"/>
      </svg>`,
      'AU': `<svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="18" fill="#012169"/>
        <path d="M0 0L24 18M24 0L0 18" stroke="#FFFFFF" stroke-width="1"/>
        <path d="M12 0V18M0 9H24" stroke="#FFFFFF" stroke-width="1"/>
        <circle cx="6" cy="4.5" r="1" fill="#FFFFFF"/>
        <circle cx="18" cy="4.5" r="1" fill="#FFFFFF"/>
        <circle cx="6" cy="13.5" r="1" fill="#FFFFFF"/>
        <circle cx="18" cy="13.5" r="1" fill="#FFFFFF"/>
        <rect x="8" y="7" width="8" height="4" fill="#FF0000"/>
      </svg>`,
      'NZ': `<svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="24" height="18" fill="#012169"/>
        <path d="M0 0L24 18M24 0L0 18" stroke="#FFFFFF" stroke-width="1"/>
        <path d="M12 0V18M0 9H24" stroke="#FFFFFF" stroke-width="1"/>
        <circle cx="6" cy="4.5" r="1" fill="#FF0000"/>
        <circle cx="18" cy="4.5" r="1" fill="#FF0000"/>
        <circle cx="6" cy="13.5" r="1" fill="#FF0000"/>
        <circle cx="18" cy="13.5" r="1" fill="#FF0000"/>
      </svg>`
    };

    return flags[countryCode] || flags['BR'];
  }

  #showLoading() {
    this.refs.loading.classList.remove('hidden');
  }

  #hideLoading() {
    this.refs.loading.classList.add('hidden');
  }

  #showError(message) {
    this.refs.error.textContent = message;
    this.refs.error.classList.remove('hidden');
  }

  #hideError() {
    this.refs.error.classList.add('hidden');
  }

  #showOptions() {
    this.refs.options.classList.remove('hidden');
  }

  #hideOptions() {
    this.refs.options.classList.add('hidden');
  }
}

if (!customElements.get('delivery-estimate-component')) {
  customElements.define('delivery-estimate-component', DeliveryEstimateComponent);
}
